/**
 * @description SwatchCarousel widget factory - standalone implementation using native scrolling
 * @param Widget Base widget for extending
 */
function SwatchCarouselClassCreator(Widget: import('widgets/Widget').TWidget) {
    /**
     * @category widgets
     * @subcategory product
     * @class SwatchCarousel
     * @augments Widget
     * @classdesc Standalone SwatchCarousel implementation using native scrolling
     */
    class SwatchCarousel extends Widget {
        private static readonly DISABLED_CLASS = 'button-disabled';

        private static readonly SELECTED_CLASS = 'm-selected';

        private static readonly IMAGE_SMALL_CLASS = 'm-image-small';

        private static readonly IMAGE_SWATCH_DECISION = 'treatment_2';

        private decision: string = '';

        private scrollContainer: HTMLElement | null = null;

        private prevButton: HTMLElement | null = null;

        private nextButton: HTMLElement | null = null;

        private keyboardEventListeners: Array<() => void> = [];

        private scrollEventListeners: Array<() => void> = [];

        private cachedSlides: Array<HTMLElement> | null = null;

        /**
         * Returns the root element for this widget.
         * Override this if your Widget base class does not provide it.
         */
        getElement(): HTMLElement | null {
            return this.ref('self').get() ?? null;
        }

        prefs() {
            return {
                rtl: false,
                optimizelyFeatureFlagKey: '',
                ...super.prefs()
            };
        }

        init() {
            this.setupScrollContainer();
            this.setupNavigationButtons();
            this.setupScrollBehavior();
            this.setupKeyboardNavigation();
            this.initOptimizely();
        }

        /**
         * @description Initializes Optimizely experiments setup on component
         * via optimizelyFeatureFlagKey.
         */
        async initOptimizely() {
            const optimizelyFeatureFlagKey = this.prefs().optimizelyFeatureFlagKey;

            if (optimizelyFeatureFlagKey) {
                this.decision = (await this.callIfExists('decideFeatureFlagVariation', optimizelyFeatureFlagKey)) as string;
                this.applyExperimentLayout();
            }
        }

        /**
         * Applies optimizely layout for experiment with key 'plp_pdp_product_image'
         */
        applyExperimentLayout() {
            if (this.decision !== SwatchCarousel.IMAGE_SWATCH_DECISION) {
                return;
            }

            const swatchesTabList = this.ref('swatchesTabList').get();

            if (!swatchesTabList) {
                return;
            }

            swatchesTabList.querySelectorAll<HTMLElement>('[data-ref="swatchButton"]').forEach(swatchButton => {
                const swatchImage = swatchButton.querySelector<HTMLElement>('[data-ref="swatchImage"]');

                if (!swatchImage) {
                    return;
                }

                swatchButton.classList.add(SwatchCarousel.IMAGE_SMALL_CLASS);

                const smallImageUrl = swatchImage.dataset.imageSmallUrl;

                if (smallImageUrl) {
                    swatchImage.style.backgroundImage = `url('${smallImageUrl}')`;
                }
            });
        }

        private setupScrollContainer() {
            const element = this.ref('self').get();

            if (!element) {
                return;
            }

            // Find scroll container
            this.scrollContainer = this.ref('swatchesTabList').get() ?? null;
        }

        private setupNavigationButtons() {
            const element = this.ref('self').get();

            if (!element || !this.scrollContainer) {
                return;
            }

            const isRtl = this.prefs().rtl;

            // Find existing navigation buttons from template
            // In RTL mode, visual prev/next are swapped
            this.prevButton = this.ref(isRtl ? 'nextSlide' : 'prevSlide').get() ?? null;
            this.nextButton = this.ref(isRtl ? 'prevSlide' : 'nextSlide').get() ?? null;

            // Add event listeners to buttons
            // In RTL mode, scrolling direction is inverted
            this.attachButtonListener(this.prevButton, isRtl ? this.scrollToNext : this.scrollToPrevious);
            this.attachButtonListener(this.nextButton, isRtl ? this.scrollToPrevious : this.scrollToNext);
        }

        private attachButtonListener(button: HTMLElement | null, handler: () => void) {
            if (!button) {
                return;
            }

            const boundHandler = handler.bind(this);

            button.addEventListener('click', boundHandler);

            this.scrollEventListeners.push(() => {
                button.removeEventListener('click', boundHandler);
            });
        }

        private setupScrollBehavior() {
            if (!this.scrollContainer) {
                return;
            }

            // Update button states on scroll
            const handleScroll = () => this.updateNavigationButtons();

            this.scrollContainer.addEventListener('scroll', handleScroll);

            this.scrollEventListeners.push(() => {
                this.scrollContainer?.removeEventListener('scroll', handleScroll);
            });

            // Initial button state
            this.updateNavigationButtons();
        }

        private updateNavigationButtons() {
            if (!this.scrollContainer || !this.prevButton || !this.nextButton) {
                return;
            }

            const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer;
            const scrollStart = Math.abs(scrollLeft);
            const isRTL = this.prefs().rtl;

            // Update prev button using existing classes
            const isAtStart = scrollStart <= 12 || (isRTL ? scrollLeft > 0 : scrollLeft < 0);

            if (isAtStart) {
                this.prevButton.classList.add(SwatchCarousel.DISABLED_CLASS);
            } else {
                this.prevButton.classList.remove(SwatchCarousel.DISABLED_CLASS);
            }

            // Update next button using existing classes
            const isAtEnd = scrollStart + clientWidth >= scrollWidth - 12;

            if (isAtEnd) {
                this.nextButton.classList.add(SwatchCarousel.DISABLED_CLASS);
            } else {
                this.nextButton.classList.remove(SwatchCarousel.DISABLED_CLASS);
            }
        }

        private scrollSlideIntoView(slide: HTMLElement) {
            slide.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }

        /**
         * Scrolls the swatch carousel by a calculated amount in the given direction.
         *
         * @param direction - The scroll direction: `-1` to scroll left (or toward the start),
         *                    `1` to scroll right (or toward the end).
         */
        private scrollBySwatch(direction: 1 | -1) {
            if (!this.scrollContainer) {
                return;
            }

            const { clientWidth, scrollLeft, scrollWidth } = this.scrollContainer;
            const halfWidth = clientWidth * 0.5;

            let absScrollLeft = Math.abs(scrollLeft) || halfWidth;
            let remaining = Math.abs(scrollWidth - Math.abs(scrollLeft) - clientWidth) || clientWidth;

            const isScrollingFurtherInCurrentDirection = (scrollLeft < 0 && direction === -1) || (scrollLeft > 0 && direction === 1);

            if (isScrollingFurtherInCurrentDirection && absScrollLeft < halfWidth) {
                absScrollLeft = halfWidth;
            }

            const isScrollingBackTowardOrigin = (scrollLeft < 0 && direction === 1) || (scrollLeft > 0 && direction === -1);

            if (isScrollingBackTowardOrigin && remaining < halfWidth) {
                remaining = halfWidth;
            }

            const scrollAmount = Math.min(absScrollLeft, remaining, halfWidth);

            this.scrollContainer.scrollBy({
                left: direction * scrollAmount,
                behavior: 'smooth'
            });
        }

        private scrollToPrevious() {
            this.scrollBySwatch(-1);
        }

        private scrollToNext() {
            this.scrollBySwatch(1);
        }

        private setupKeyboardNavigation() {
            const container = this.ref('self').get();

            if (!container) {
                return;
            }

            this.setupInitialFocus();

            const handleKeyDown = this.handleKeyDown.bind(this);

            container.addEventListener('keydown', handleKeyDown);

            this.keyboardEventListeners.push(() => {
                container.removeEventListener('keydown', handleKeyDown);
            });
        }

        private setupInitialFocus() {
            const slides = this.getAllSlides();

            if (slides.length === 0) {
                return;
            }

            const activeSlide = slides.find(slide => slide.classList.contains(SwatchCarousel.SELECTED_CLASS)) || slides[0];

            slides.forEach(slide => {
                const isActive = slide === activeSlide;

                slide.setAttribute('tabindex', isActive ? '0' : '-1');
            });
        }

        private getAllSlides(): Array<HTMLElement> {
            if (this.cachedSlides) {
                return this.cachedSlides;
            }

            const container = this.ref('self').get();

            if (!container) {
                return [];
            }

            this.cachedSlides = Array.from(container.querySelectorAll('.b-product_tile_swatches-swatch'));

            return this.cachedSlides;
        }

        private getCurrentFocusedSlide(): HTMLElement | null {
            const slides = this.getAllSlides();

            return slides.find(slide => slide.classList.contains(SwatchCarousel.SELECTED_CLASS)) || null;
        }

        private focusSlide(slide: HTMLElement, shouldActivate: boolean = false) {
            // Set all slides to tabindex -1, only the focused slide gets tabindex 0
            this.getAllSlides().forEach(s => {
                const isCurrentSlide = s === slide;

                s.setAttribute('tabindex', isCurrentSlide ? '0' : '-1');
            });

            slide.focus();

            if (shouldActivate) {
                this.activateSlide(slide);
            }

            setTimeout(() => {
                // Center the slide when using keyboard navigation
                this.scrollSlideIntoView(slide);
            }, 60);
        }

        private activateSlide(slide: HTMLElement) {
            const mouseEnterEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
            });

            slide.dispatchEvent(mouseEnterEvent);
        }

        private handleKeyDown(event: KeyboardEvent) {
            // Check if the event target is within our carousel
            const target = event.target as HTMLElement;
            const container = this.getElement();

            if (!container || !container.contains(target)) {
                return;
            }

            const currentSlide = this.getCurrentFocusedSlide();

            if (!currentSlide) {
                return;
            }

            const slides = this.getAllSlides();
            const currentIndex = slides.indexOf(currentSlide);

            let targetIndex = -1;
            let shouldActivate = false;

            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();

                    targetIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1;
                    shouldActivate = true;
                    break;

                case 'ArrowRight':
                    event.preventDefault();

                    targetIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : 0;
                    shouldActivate = true;
                    break;

                case 'Home':
                    event.preventDefault();

                    targetIndex = 0;
                    shouldActivate = true;
                    break;

                case 'End':
                    event.preventDefault();

                    targetIndex = slides.length - 1;
                    shouldActivate = true;
                    break;

                default:
                    return;
            }

            if (targetIndex >= 0 && targetIndex < slides.length) {
                const targetSlide = slides[targetIndex];

                this.focusSlide(targetSlide, shouldActivate && !targetSlide.classList.contains(SwatchCarousel.SELECTED_CLASS));
            }
        }
    }

    return SwatchCarousel;
}

export type TSwatchCarousel = ReturnType<typeof SwatchCarouselClassCreator>;
export type TSwatchCarouselInstance = InstanceType<TSwatchCarousel>;

export default SwatchCarouselClassCreator;
